import { useQuery } from "@tanstack/react-query";
import { Activity, AppWindow, ClipboardCheck, Fingerprint, KeyRound, ShieldCheck } from "lucide-react";

import { CurrentSession, DeveloperApplication, DeviceNamespace, api, formatDate } from "../api";
import { ErrorState, LoadingState, PageHeader, Section, StatusBadge } from "../account/components";
import { DeveloperPageProps } from "./DeveloperCenter";

export function OverviewPage({ organization, session }: DeveloperPageProps & { session: CurrentSession }) {
  const canReadApplications = organization.permissions.includes("applications.read");
  const canReadDevices = organization.permissions.some((item) => item === "devices.read" || item === "devices.manage");
  const applications = useQuery({
    queryKey: ["developer-applications", organization.id],
    queryFn: () => api<{ applications: DeveloperApplication[] }>(`/api/v1/organizations/${organization.id}/applications`),
    enabled: canReadApplications,
  });
  const namespaces = useQuery({
    queryKey: ["device-namespaces", organization.id],
    queryFn: () => api<{ namespaces: DeviceNamespace[] }>(`/api/v1/organizations/${organization.id}/device-namespaces`),
    enabled: canReadDevices,
  });

  return (
    <>
      <PageHeader eyebrow="DEVELOPER" title="开发者总览" description={`${organization.displayName} 的身份接入与原生客户端运行状态。`} action={<StatusBadge tone="good">{organization.status}</StatusBadge>} />
      {(applications.isPending && canReadApplications) || (namespaces.isPending && canReadDevices) ? <LoadingState /> : null}
      {applications.error && <ErrorState error={applications.error} retry={() => void applications.refetch()} />}
      {namespaces.error && <ErrorState error={namespaces.error} retry={() => void namespaces.refetch()} />}
      <div className="developer-metrics">
        <div><AppWindow /><span>活动应用</span><strong>{applications.data?.applications.length ?? "-"}</strong></div>
        <div><KeyRound /><span>应用环境</span><strong>{applications.data?.applications.length == null ? "-" : "按应用查看"}</strong></div>
        <div><Fingerprint /><span>设备 Namespace</span><strong>{namespaces.data?.namespaces.length ?? "-"}</strong></div>
        <div><Activity /><span>当前会话</span><strong>AAL{session.assuranceLevel}</strong></div>
      </div>
      <Section title="权限边界" description="当前 membership 在开发者中心生效的组织权限。">
        <div className="permission-matrix">
          {organization.permissions.map((permission) => <StatusBadge key={permission} tone={permission.endsWith(".manage") ? "warning" : "neutral"}>{permission}</StatusBadge>)}
        </div>
      </Section>
      <Section title="接入状态" description="生产流量需要协议配置、设备策略和独立审核共同满足。">
        <div className="readiness-list">
          <div><ShieldCheck /><span><strong>组织边界</strong><small>角色与权限已从服务端会话解析</small></span><StatusBadge tone="good">已启用</StatusBadge></div>
          <div><AppWindow /><span><strong>OIDC 应用</strong><small>Authorization Code + PKCE、JWKS 与 UserInfo</small></span><StatusBadge tone={canReadApplications ? "good" : "warning"}>{canReadApplications ? "可管理" : "无权限"}</StatusBadge></div>
          <div><Fingerprint /><span><strong>原生客户端</strong><small>HWID 仅由原生 SDK 采集，浏览器不读取硬件标识</small></span><StatusBadge tone={canReadDevices ? "good" : "neutral"}>{canReadDevices ? "可管理" : "未授权"}</StatusBadge></div>
          <div><ClipboardCheck /><span><strong>近期验证</strong><small>{formatDate(session.stepUpAt)}</small></span><StatusBadge tone={session.stepUpAt ? "good" : "warning"}>{session.stepUpAt ? "已验证" : "只读"}</StatusBadge></div>
        </div>
      </Section>
    </>
  );
}
